import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Alert, Button,
  LinearProgress, Divider
} from '@mui/material';
import { AccountBalance, CreditCard, History } from '@mui/icons-material';
import api from '../../services/api';
import { useAuthStore } from '../../store/auth';
import { PaymentDialog } from '../payments/PaymentDialog';

export const CustomerBalancePage: React.FC = () => {
  const [balance, setBalance] = useState<any>(null);
  const [txns, setTxns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [payDialog, setPayDialog] = useState(false);
  const [fakeInvoice, setFakeInvoice] = useState<any>(null);
  const [amount, setAmount] = useState('');

  const { user } = useAuthStore();

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/billing/balance'),
      api.get('/billing/transactions?limit=20'),
    ]).then(([b, t]) => {
      setBalance(b.data);
      setTxns(t.data?.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openTopup = (amt: number) => {
    // Create a virtual invoice object for the payment dialog
    setFakeInvoice({
      id: null,
      invoice_number: 'Balance Top-Up',
      total: amt,
      tenant_id: user?.tenant_id || null,
      status: 'draft',
    });
    setPayDialog(true);
  };

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  const usedPct = balance?.credit_limit > 0
    ? Math.min(100, ((balance.credit_limit - balance.balance) / balance.credit_limit) * 100)
    : 0;

  const TYPE_COLOR: any = { credit: 'success', debit: 'error', refund: 'info', adjustment: 'warning' };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <AccountBalance sx={{ color: 'primary.main', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Balance & Payments</Typography>
          <Typography variant="body2" color="text.secondary">Top up your account and view transaction history</Typography>
        </Box>
      </Box>

      {/* Balance summary */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Available Balance</Typography>
              <Typography variant="h3" fontWeight={700}>${Number(balance?.balance || 0).toFixed(2)}</Typography>
              <Chip label={balance?.billing_type || 'prepaid'} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', mt: 1 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Credit Limit</Typography>
              <Typography variant="h5" fontWeight={600}>${Number(balance?.credit_limit || 0).toFixed(2)}</Typography>
              <LinearProgress variant="determinate" value={usedPct}
                sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: usedPct > 80 ? '#ff5252' : 'white' } }} />
              <Typography variant="caption" sx={{ opacity: 0.75 }}>{usedPct.toFixed(0)}% used</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" sx={{ opacity: 0.85, mb: 1 }}>Quick Top-Up</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[50, 100, 200, 500].map(amt => (
                  <Button key={amt} variant="outlined" size="small" onClick={() => openTopup(amt)}
                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                    ${amt}
                  </Button>
                ))}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Custom amount top-up */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>Add Balance</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add balance to your account using credit card or PayPal
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <input
              type="number"
              placeholder="Enter amount..."
              value={amount}
              onChange={e => setAmount(e.target.value)}
              style={{ padding: '12px 16px', border: '1px solid #ddd', borderRadius: 8, fontSize: 16, width: 180 }}
            />
            <Button variant="contained" size="large" startIcon={<CreditCard />}
              onClick={() => { if (parseFloat(amount) > 0) openTopup(parseFloat(amount)); }}
              disabled={!amount || parseFloat(amount) <= 0}>
              Pay Now
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Transaction history */}
      <Card>
        <CardContent sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <History color="primary" />
            <Typography variant="h6" fontWeight={700}>Recent Transactions</Typography>
          </Box>
        </CardContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Date', 'Type', 'Amount', 'Description'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {txns.map((row, i) => (
              <TableRow key={row.id || i} hover>
                <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap', fontSize: 12 }}>
                  {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                </TableCell>
                <TableCell><Chip label={row.type || row.transaction_type || '—'} size="small" color={TYPE_COLOR[row.type] || 'default'} /></TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: Number(row.amount) >= 0 ? 'success.main' : 'error.main' }}>
                  {Number(row.amount) >= 0 ? '+' : ''}{Number(row.amount || 0).toFixed(4)}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{row.description || row.notes || '—'}</TableCell>
              </TableRow>
            ))}
            {txns.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No transactions yet.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <PaymentDialog
        open={payDialog}
        invoice={fakeInvoice}
        onClose={() => setPayDialog(false)}
        onSuccess={() => { load(); setAmount(''); }}
      />
    </Box>
  );
};
